import { tokens } from "../tokens/generated/tokens";

import type { Theme } from "@mui/material/styles";

/**
 * The design system's brand colours, and the accessor base components use to
 * read them.
 *
 * `palette.brand` exists because `primary` and `secondary` name opposite
 * colours in this theme and in Broker Portal's — see the note on `Palette` in
 * `shared.tsx`. Components read `brand` so they render the same colour either
 * way.
 *
 * Values come from `tokens/generated` (design's Figma export), so there is one
 * source of truth shared with `kaizenTheme` rather than hand-copied hex.
 *
 * These also serve as the fallback for themes that predate `brand` — a bare
 * `createTheme()`, or a consuming app that builds its own. Without it those
 * mount points would throw on a missing key rather than merely look wrong.
 */
export const BRAND_COLORS = {
  /** The Centro purple. */
  primary: tokens.theme.light.fgColor.primary,
  /** Darkened purple for hover and active states. */
  primaryHover: tokens.baseColorLight.color.purple["900"],
  /**
   * The readable text/icon colour on a filled brand-primary surface. The brand
   * purple is dark in light mode and light in dark mode, so this flips with it:
   * white on the light-mode button, near-black on the dark-mode button.
   */
  onPrimary: tokens.theme.light.fgColor.onEmphasis,
  /** The supporting teal. */
  supporting: tokens.theme.light.fgColor.secondary,
};

export type BrandColor = keyof typeof BRAND_COLORS;

/**
 * Resolves a brand colour from the active theme, for use in an `sx` value.
 *
 * Prefers `vars` over `palette`: a theme with `cssVariables` keeps resolved
 * literals on `palette` and the custom property references on `vars`, so
 * reading `palette` would pin the colour to whichever scheme is the default.
 * Falling through to `palette` covers themes that emit no properties, and
 * `BRAND_COLORS` covers themes predating `brand` altogether.
 *
 * The result is a CSS colour, not always a hex one. It cannot be passed to
 * MUI's `alpha()` or `darken()`, which throw on a custom property — use
 * `paletteVarAlpha` for transparency.
 */
export const brand =
  (color: BrandColor) =>
  (theme: Theme): string =>
    theme.vars?.palette.brand?.[color] ??
    theme.palette.brand?.[color] ??
    BRAND_COLORS[color];
